import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Alert,
  Box,
  Button,
  FormGroup,
  IconButton,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import {
  CsConfigurationApi,
  useCsCfgObject,
  useCsConfiguration,
  useCsConfigurationApi,
} from "@plansoft-configuration-renderer/framework";
import { GatewayDest } from "@plansoft-configuration-renderer/gateway";

import ResetIcon from "@/assets/svgs/reset.svg?react";
import { CustomConfigurationTreeCompleteness } from "@/renderer/components/CustomConfigurationTree/CustomConfigurationTreeCompleteness";
import { useSession } from "@/renderer/hooks/useSession";
import {
  CadFormData,
  PropertyValue,
  downloadFile,
  fetchCadBox,
  fetchCheckDocument,
  fetchDownloadDocument,
  fetchGenerateDocument,
  saveConfiguration,
  sendAllRequests,
} from "@/tools/helper";

import CadAutoSubmitForm from "./CadAutoSubmitForm";
import ConfigurationActions from "./ConfigurationActions";

const DOCUMENT_TIMEOUT_MS = 60000;

export default function ConfigurationHeader(): ReactNode {
  const { t } = useTranslation();
  const { palette } = useTheme();

  const { loadConfiguration } = useCsConfigurationApi();
  const {
    id: sessionId,
    properties,
    localeId,
    context,
    currencyId,
  } = useCsConfiguration();
  const { typeCode: cfgTypeCode, isComplete } = useCsCfgObject();
  const { reset, isResetDisabled } = useSession();

  const [typeCode, setTypeCode] = useState<string | null>(cfgTypeCode ?? null);
  const [cadFormData, setCadFormData] = useState<CadFormData | null>(null);
  const [documentGeneratingErrorMessage, setDocumentGeneratingErrorMessage] =
    useState("");
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [isGeneratingDrawing, setIsGeneratingDrawing] = useState(false);

  useEffect(() => {
    setTypeCode(cfgTypeCode ?? null);
  }, [cfgTypeCode]);

  const handleTypecodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setTypeCode(event.target.value === "" ? null : event.target.value);
  };

  const loadTypeCode = async (): Promise<void> => {
    if (typeCode) {
      loadConfiguration({
        typeCode,
        context,
        localeId,
        currencyId,
      });
    }
  };

  const handleTypecodeKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      loadTypeCode();
    }
  };

  const handleAddToCartClick = (): void => {
    handleSaveClick("SC");
  };

  const handleCadClick = async (): Promise<void> => {
    try {
      const data = await fetchCadBox();
      setCadFormData(data);
    } catch (error) {
      console.error("Error fetching CAD box:", error);
    }
  };

  const handleDrawingClick = async (api: CsConfigurationApi): Promise<void> => {
    setIsGeneratingDrawing(true);
    try {
      const response = await api.invokeUserDefinedFunction({
        name: "GetDimSheetData",
        path: "",
        input: "{}",
      });
      if (!response) {
        console.error("No response received from the server.");
        return;
      }
      const responseData = JSON.parse(response);
      await sendAllRequests(responseData);
    } finally {
      setIsGeneratingDrawing(false);
    }
  };

  const handleGenerateDocumentClick = async (): Promise<void> => {
    setIsGeneratingDocument(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DOCUMENT_TIMEOUT_MS);

    try {
      await Promise.race([
        (async (): Promise<void> => {
          const generatedDoc = await fetchGenerateDocument({
            configId: sessionId,
            whichDocument: typeCode ?? "",
          });

          let checkFinished = false;
          while (!checkFinished) {
            if (controller.signal.aborted) throw new Error("timeout");

            const checkStatus = await fetchCheckDocument({
              pollUrl: generatedDoc.filename,
            });

            if (checkStatus.error && checkStatus.msg.length > 0) {
              setDocumentGeneratingErrorMessage(checkStatus.msg);
              return;
            }

            if (checkStatus.finish) {
              checkFinished = true;
            } else {
              await new Promise((resolve) =>
                setTimeout(resolve, checkStatus.nextCallInSec * 1000),
              );
            }
          }

          const documentData = await fetchDownloadDocument({
            pollUrl: generatedDoc.filename,
          });
          if (documentData.fileStream) {
            downloadFile(
              generatedDoc.filename,
              documentData.fileStream,
              "application/pdf",
            );
          }
        })(),
        new Promise<never>((_, reject) => {
          controller.signal.addEventListener("abort", () =>
            reject(new Error("Document generation timed out")),
          );
        }),
      ]);
    } catch (error) {
      if (error instanceof Error && error.message.includes("timed out")) {
        setDocumentGeneratingErrorMessage(
          "Document generation timed out. Please try again.",
        );
      }
    } finally {
      clearTimeout(timeoutId);
      setIsGeneratingDocument(false);
    }
  };

  const handleSaveClick = async (
    type: "FAV" | "SC" | "SAP",
  ): Promise<string> => {
    const body = {
      description: "TEST_KS",
      title: "",
      shorttext: "",
      sapOrShop: type === "SAP" ? "SAP" : "SHOP",
      type,
    };

    return await saveConfiguration(body, type, sessionId);
  };

  const handleResetClick = async (): Promise<void> => {
    try {
      await reset();
    } catch (error) {
      console.error("Failed to reset configuration:", error);
    }
  };

  return (
    <>
      <Snackbar
        open={documentGeneratingErrorMessage.length > 0}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setDocumentGeneratingErrorMessage("")}
      >
        <Alert
          onClose={() => setDocumentGeneratingErrorMessage("")}
          severity="error"
          variant="filled"
        >
          {t(documentGeneratingErrorMessage)}
        </Alert>
      </Snackbar>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          borderBottom: "1px solid",
          borderColor: palette.grey[500],
          width: "100%",
          maxWidth: "1400px",
          margin: "0 auto",
          paddingY: "1rem",
          zIndex: 100,
          backgroundColor: palette.background.default,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              "@media (min-width:1400px)": {
                flexDirection: "row",
                gap: 4,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <CustomConfigurationTreeCompleteness
                sx={{ ml: 1 }}
                completed={isComplete}
              />
              <GatewayDest name="custom-configuration-tree-button" />
              <Typography
                // [KS] h1 variant needs to be preserved according to HIWIN's requirement
                variant="h1"
                sx={{
                  color: palette.primary.main,
                  fontSize: "1.375rem !important",
                  alignSelf: "center",
                  fontWeight: "bold",
                }}
              >
                {t("title")}
              </Typography>
              <GatewayDest name="custom-configuration-tree-pin" />
              <Tooltip title={t("reset_configuration")} arrow>
                <IconButton
                  onClick={handleResetClick}
                  disabled={isResetDisabled}
                >
                  <SvgIcon component={ResetIcon} inheritViewBox />
                </IconButton>
              </Tooltip>
              <GatewayDest name="custom-configuration-tree-content" />
            </Box>

            <FormGroup
              sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography sx={{ alignSelf: "center" }}>
                {t("type_key")}
              </Typography>
              <TextField
                id="outlined-basic"
                variant="outlined"
                size={"extraSmall"}
                sx={{ width: "380px" }}
                value={typeCode}
                onChange={handleTypecodeChange}
                onKeyDown={handleTypecodeKeyDown}
              />
              <Button
                variant="outlined"
                onClick={loadTypeCode}
                sx={{ width: "80px" }}
              >
                {t("start")}
              </Button>
            </FormGroup>
          </Box>
          <ConfigurationActions
            properties={properties as Record<string, PropertyValue>}
            onCadClick={handleCadClick}
            onDrawingClick={handleDrawingClick}
            isGeneratingDrawing={isGeneratingDrawing}
            onSaveClick={handleSaveClick}
            onAddToCartClick={handleAddToCartClick}
            onGenerateDocumentClick={handleGenerateDocumentClick}
            isGeneratingDocument={isGeneratingDocument}
          />
        </Box>
      </Box>

      <CadAutoSubmitForm cadFormData={cadFormData} />
    </>
  );
}
